export type Dir = 'across' | 'down';
export type Cell = { r: number; c: number };

export type Clue = {
  number: number;
  dir: Dir;
  clue: string;
  answer: string;
  cells: Cell[];
};

export type Puzzle = {
  title: string;
  rows: number;
  cols: number;
  /** Solution grid; `null` marks a blocked (black) square. */
  solution: (string | null)[][];
  /** Entry number keyed by `"r-c"`, for the little corner labels. */
  numberAt: Record<string, number>;
  whiteCells: { r: number; c: number; solution: string }[];
  across: Clue[];
  down: Clue[];
};

type RawPuzzle = {
  title: string;
  solution: (string | null)[][];
  across: Record<number, string>;
  down: Record<number, string>;
};

export const key = (r: number, c: number) => `${r}-${c}`;

/**
 * The K & B mini — all clues are facts about the couple. Interlocking on the
 * shared letters of HOTSAUCE. Swap `RAW` for any grid + clues; the numbering,
 * entries, and share card all derive from it automatically. `null` = block.
 */
const B = null;
const RAW: RawPuzzle = {
  title: 'The K & B Mini',
  solution: [
    [B, 'P', B, B, B, B, B, B, B, B, B, B],
    [B, 'I', B, B, B, B, B, B, B, B, B, B],
    [B, 'A', 'D', 'M', 'O', B, B, B, B, B, B, B],
    [B, 'N', B, B, B, B, B, B, B, B, B, B],
    ['H', 'O', 'T', 'S', 'A', 'U', 'C', 'E', B, B, B, B],
    [B, B, B, 'E', B, B, 'V', B, B, B, B, B],
    [B, 'C', 'A', 'V', 'A', 'L', 'I', 'E', 'R', 'S', B, B],
    [B, B, B, 'E', B, B, 'L', B, B, B, B, B],
    [B, B, B, 'N', 'E', 'W', 'L', 'Y', 'W', 'E', 'D', 'S'],
    [B, B, B, B, B, B, 'E', B, B, B, B, B],
  ],
  across: {
    2: 'Colloquial name for the neighborhood where Ben and Karishma live. (Hint: it’s Adams Morgan 😉)',
    3: 'The couple’s favorite type of condiment',
    6: 'The official mascot of Ben and Karishma’s alma mater',
    7: 'What Ben and Karishma will be on May 1st',
  },
  down: {
    1: 'The instrument both Ben and Karishma play',
    4: 'Number of years Ben and Karishma dated before getting engaged!',
    5: 'Shorthand for the city where Ben and Karishma got engaged (and met)!',
  },
};

export function buildPuzzle(raw: RawPuzzle = RAW): Puzzle {
  const { solution } = raw;
  const rows = solution.length;
  const cols = solution[0]?.length ?? 0;
  const isWhite = (r: number, c: number) => Boolean(solution[r]?.[c]);

  const numberAt: Record<string, number> = {};
  const whiteCells: { r: number; c: number; solution: string }[] = [];
  const across: Clue[] = [];
  const down: Clue[] = [];
  let n = 0;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const s = solution[r]?.[c];
      if (!s) continue;
      whiteCells.push({ r, c, solution: s });

      const startsAcross = !isWhite(r, c - 1) && isWhite(r, c + 1);
      const startsDown = !isWhite(r - 1, c) && isWhite(r + 1, c);
      if (startsAcross || startsDown) {
        n += 1;
        numberAt[key(r, c)] = n;
      }

      if (startsAcross) {
        const cells: Cell[] = [];
        let answer = '';
        for (let cc = c; isWhite(r, cc); cc++) {
          cells.push({ r, c: cc });
          answer += solution[r]?.[cc] ?? '';
        }
        across.push({ number: n, dir: 'across', clue: raw.across[n] ?? '', answer, cells });
      }

      if (startsDown) {
        const cells: Cell[] = [];
        let answer = '';
        for (let rr = r; isWhite(rr, c); rr++) {
          cells.push({ r: rr, c });
          answer += solution[rr]?.[c] ?? '';
        }
        down.push({ number: n, dir: 'down', clue: raw.down[n] ?? '', answer, cells });
      }
    }
  }

  return { title: raw.title, rows, cols, solution, numberAt, whiteCells, across, down };
}

export const PUZZLE = buildPuzzle();
